import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { ArrowRight, BookOpen, Layers, Scale, Users, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';
interface TheoryEntry {
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  tags: string[];
  color: string;
}
const theories: TheoryEntry[] = [{
  title: 'The Fat Spectrum & Size Privilege',
  description: 'Understanding how size affects access, privilege, and oppression within the fat community — from smallfat to infinifat.',
  path: '/clusters/fat-frameworks',
  icon: <Layers className="h-8 w-8" data-id="element-1425" />,
  tags: ['Framework', 'Access', 'Privilege'],
  color: 'border-brand-magenta'
}, {
  title: 'Categories of Fat Access',
  description: 'A framework defining the key domains where fat people face barriers — from physical space to healthcare to cultural representation.',
  path: '/clusters/categories-of-fat-access',
  icon: <div className="h-8 w-8" data-id="element-1426" />,
  tags: ['Framework', 'Access', 'Structural'],
  color: 'border-brand-cyan'
}];
const furtherTheories = [{
  title: 'Health at Every Size (HAES)',
  author: 'Lindo Bacon / ASDAH',
  description: 'A weight-neutral approach to health that challenges the medicalisation of fatness and promotes body autonomy.',
  type: 'Framework'
}, {
  title: 'Fat Activism as Social Movement',
  author: 'Charlotte Cooper',
  description: 'An analysis of fat activism as a diverse, creative, and politically radical social movement with roots in queer and feminist organising.',
  type: 'Theory'
}, {
  title: 'Anti-Fatness as Anti-Blackness',
  author: "Sabrina Strings / Da'Shaun L. Harrison",
  description: 'The argument that modern fatphobia is rooted in anti-Black racism, colonial beauty standards, and the racialised regulation of bodies.',
  type: 'Theory'
}, {
  title: 'Radical Body Politics',
  author: 'Sonya Renee Taylor',
  description: 'A framework for understanding body hierarchy as a tool of systemic oppression, and radical self-love as a political practice.',
  type: 'Framework'
}, {
  title: 'Disability Justice & Fatness',
  author: 'Sins Invalid / Leah Lakshmi Piepzna-Samarasinha',
  description: 'The intersection of fat liberation and disability justice — shared struggles around access, medicalisation, and bodily autonomy.',
  type: 'Intersection'
}, {
  title: 'Fat Pedagogy',
  author: 'Erin Cameron & Constance Russell',
  description: 'An emerging field examining how anti-fatness operates in educational settings and how educators can teach fat justice.',
  type: 'Methodology'
}];
export function FatTheoryCatalogue() {
  return <div className="min-h-screen bg-brand-cream" data-id="element-1427">
      <HeroSection title="Fat Theory Catalogue" subtitle="A growing collection of frameworks, theories, and critical perspectives in fat liberation." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1428" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1429">
        <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1430">
          Fat liberation draws on a rich body of theory — from activist
          frameworks to academic research to lived experience. This catalogue
          collects the key{' '}
          <strong className="text-brand-magenta" data-id="element-1431">
            frameworks and perspectives
          </strong>{' '}
          we engage with at ReGrowth, with links to our in-depth explorations.
        </p>
        <p className="text-lg text-gray-600 mt-4" data-id="element-1432">
          This is a living resource. We'll continue adding frameworks as our
          work develops.
        </p>
      </section>

      {/* ReGrowth Theory Pages */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1433">
        <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-1434">
          Explore In Depth
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1435">
          {theories.map((theory, index) => <Link key={theory.path} to={theory.path} className="group block" data-id="element-1436">
              <motion.article initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`bg-white border-2 border-brand-black border-l-8 ${theory.color} p-8 hover:shadow-xl transition-shadow h-full flex flex-col`} data-id="element-1437">
                <div className="text-brand-magenta mb-4" data-id="element-1438">{theory.icon}</div>
                <h3 className="text-2xl font-black uppercase mb-3 text-brand-black group-hover:text-brand-magenta transition-colors leading-tight" data-id="element-1439">
                  {theory.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6 flex-grow" data-id="element-1440">
                  {theory.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-1441">
                  <div className="flex flex-wrap gap-2" data-id="element-1442">
                    {theory.tags.map(tag => <span key={tag} className="text-xs font-bold uppercase tracking-wider text-brand-magenta bg-brand-magenta/10 px-2 py-1" data-id="element-1443">
                        {tag}
                      </span>)}
                  </div>
                  <ArrowRight className="h-5 w-5 text-brand-magenta group-hover:translate-x-1 transition-transform" data-id="element-1444" />
                </div>
              </motion.article>
            </Link>)}
        </div>
      </section>

      {/* Wider Theories & Frameworks */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1445">
        <h2 className="text-2xl font-black uppercase mb-4 text-brand-black" data-id="element-1446">
          Wider Theories & Frameworks
        </h2>
        <p className="text-gray-600 mb-8 max-w-3xl" data-id="element-1447">
          Key theoretical perspectives from across fat studies, disability
          justice, and critical race theory that inform fat liberation work.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1448">
          {furtherTheories.map((theory, index) => <motion.div key={theory.title} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.06
        }} className="bg-white border border-gray-200 p-6 flex flex-col" data-id="element-1449">
              <div className="flex items-center gap-2 mb-3" data-id="element-1450">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-magenta bg-brand-magenta/10 px-2 py-1" data-id="element-1451">
                  {theory.type}
                </span>
              </div>
              <h3 className="text-lg font-bold text-brand-black mb-1 leading-snug" data-id="element-1452">
                {theory.title}
              </h3>
              <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-1453">
                {theory.author}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed flex-grow" data-id="element-1454">
                {theory.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-magenta text-white" data-id="element-1455">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1456">
          <BookOpen className="h-10 w-10 mx-auto mb-6 opacity-80" data-id="element-1457" />
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-1458">
            Want to Go Deeper?
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-1459">
            Our courses and reading lists build on these frameworks with guided
            learning experiences.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1460">
            <Link to="/clusters/courses" className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-magenta font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-1461">
              Browse Courses
            </Link>
            <Link to="/clusters/read-waestm" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold uppercase tracking-wide hover:bg-white hover:text-brand-magenta transition-colors" data-id="element-1462">
              Read WAESTM
            </Link>
          </div>
        </div>
      </section>
    </div>;
}